// FILE: src/components/OrderEditorDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../Orders.css";

const editableStatuses = ["pending", "order_confirmed", "preparing", "order_placed"];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(value || 0);

const toEditableItems = (items) =>
  (items || []).map((item) => ({
    ...item,
    quantity: item.quantity ?? 1,
  }));

const round2 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const calculateSubtotalFromItems = (items) =>
  round2(
    (items || []).reduce(
      (sum, item) => sum + (Number(item?.price || 0) * Number(item?.quantity || 0)),
      0
    )
  );

const normalizeTaxRate = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed > 1 && parsed <= 100) return parsed / 100;
  return parsed >= 0 ? parsed : null;
};

const getOriginalSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return round2(order.subtotal);
  if (typeof order?.subTotal === "number") return round2(order.subTotal);
  if (typeof order?.itemSubtotal === "number") return round2(order.itemSubtotal);
  return calculateSubtotalFromItems(order?.items || []);
};

const getOriginalTaxAmount = (order) => {
  if (typeof order?.taxAmount === "number") return round2(order.taxAmount);
  if (typeof order?.tax === "number") return round2(order.tax);
  return 0;
};

const getOriginalTotal = (order) => {
  if (typeof order?.total === "number") return round2(order.total);
  if (typeof order?.totalAmount === "number") return round2(order.totalAmount);
  if (typeof order?.grandTotal === "number") return round2(order.grandTotal);
  if (typeof order?.subtotal === "number") return round2(order.subtotal);
  return calculateSubtotalFromItems(order?.items || []);
};

const getEffectiveTaxRate = (order, originalSubtotal, originalTaxAmount) => {
  const explicitRate = normalizeTaxRate(order?.taxRate);
  if (explicitRate != null) return explicitRate;
  if (originalSubtotal > 0 && originalTaxAmount > 0) {
    return round2(originalTaxAmount / originalSubtotal);
  }
  return 0;
};

const calculateTotals = (items, order) => {
  const subtotal = calculateSubtotalFromItems(items);
  const originalSubtotal = getOriginalSubtotal(order);
  const originalTaxAmount = getOriginalTaxAmount(order);
  const originalTotal = getOriginalTotal(order);
  const taxRate = getEffectiveTaxRate(order, originalSubtotal, originalTaxAmount);
  const preservedNonTaxCharges = round2(
    Math.max(0, originalTotal - originalSubtotal - originalTaxAmount)
  );
  const taxAmount = round2(subtotal * taxRate);
  const total = round2(subtotal + taxAmount + preservedNonTaxCharges);
  return { subtotal, taxRate, taxAmount, total, preservedNonTaxCharges };
};

const isPickupOrder = (order) => {
  const ft = String(order?.fulfillmentType || order?.deliveryMode || "").toLowerCase();
  const deliveryOption = String(order?.deliveryOption || "").toLowerCase();
  return ft === "pickup" || deliveryOption === "pickup";
};

// ✅ NEW: Scheduled vs ASAP label for restaurant drawer
const getRestaurantOrderTimeLabel = (order) => {
  const timeMode = String(order?.timeMode || "").toLowerCase(); // "asap" | "later"
  const isScheduled = !!order?.isScheduled || timeMode === "later";

  const label = String(
    order?.scheduledTimeLabel || order?.timeLabel || order?.etaLabel || ""
  ).trim();

  if (isScheduled && label) return label;     // e.g. "Wed, Dec 24 • 2:00 AM – 2:30 AM"
  if (isScheduled) return "Scheduled";
  return label && /^asap/i.test(label) ? label : "ASAP";
};

const OrderEditorDrawer = ({ open, onClose, order, restaurantId, onSaved }) => {
  const [editableItems, setEditableItems] = useState(() => toEditableItems(order?.items));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditableItems(toEditableItems(order?.items));
    setSaving(false);
  }, [order]);

  const canEdit =
    !!order && editableStatuses.includes((order.status || "").toLowerCase());

  const totals = useMemo(() => calculateTotals(editableItems, order), [editableItems, order]);

  const handleIncrease = (index) => {
    if (!canEdit) return;
    setEditableItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? { ...item, quantity: Math.max(1, (item.quantity || 1) + 1) }
          : item
      )
    );
  };

  const handleDecrease = (index) => {
    if (!canEdit) return;
    setEditableItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? { ...item, quantity: Math.max(1, (item.quantity || 1) - 1) }
          : item
      )
    );
  };

  const handleRemove = (index) => {
    if (!canEdit) return;
    setEditableItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    if (!order || !restaurantId || !canEdit || saving) return;

    if (editableItems.length === 0) {
      alert("Order must contain at least 1 item.");
      return;
    }

    const { subtotal, taxRate, taxAmount, total, preservedNonTaxCharges } = totals;
    const ref = doc(db, "orders", order.id);

    setSaving(true);
    try {
      await updateDoc(ref, {
        items: editableItems,
        subtotal,
        subTotal: subtotal,
        itemSubtotal: subtotal,
        taxRate,
        taxAmount,
        tax: taxAmount,
        total,
        totalAmount: total,
        grandTotal: total,
        updatedAt: serverTimestamp(),
      });

      if (onSaved) {
        onSaved({
          ...order,
          items: editableItems,
          subtotal,
          taxRate,
          taxAmount,
          total,
          preservedNonTaxCharges,
        });
      }
      if (onClose) onClose();
    } catch (error) {
      console.error("Failed to save order edits", error);
      alert("Could not save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !order) return null;

  const orderCode = order.orderNumber || (order.id ? `#${order.id.slice(0, 6)}` : "Order");
  const pickup = isPickupOrder(order);
  const cutleryYes = !!order.cutleryRequested || String(order.cutleryRequested).toLowerCase() === "yes";
  const driverName = (order.driverName || "").trim();

  // ✅ NEW: show correct scheduled/asap label
  const timeLabel = getRestaurantOrderTimeLabel(order);

  return (
    <div className="order-editor-overlay" role="dialog" aria-modal="true">
      <div className="order-editor-panel">
        <div className="order-editor-header">
          <div>
            <div className="order-editor-title">{canEdit ? "Edit order" : "View order"}</div>
            <div className="order-editor-subtitle">{orderCode}</div>

            <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13 }}>
              <div>
                <strong>Fulfillment:</strong> {pickup ? "Pickup" : "Delivery"}
              </div>

              {/* ✅ NEW: Scheduled/ASAP time display */}
              <div>
                <strong>Time:</strong> {timeLabel}
              </div>

              <div>
                <strong>Cutlery:</strong> {cutleryYes ? "Yes" : "No"}
              </div>
              {order.driverId ? (
                <div>
                  <strong>Driver:</strong> {driverName || order.driverId}
                </div>
              ) : null}
            </div>
          </div>

          <button type="button" className="order-editor-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="order-editor-body">
          <div className="order-editor-section-heading">Items</div>
          <div className="order-editor-items">
            {editableItems.length === 0 ? (
              <div className="incoming-empty">No items on this order.</div>
            ) : (
              editableItems.map((item, idx) => {
                const lineTotal =
                  item.price != null ? (item.price || 0) * (item.quantity || 1) : null;
                return (
                  <div key={idx} className="incoming-item-row order-editor-row">
                    <div className="incoming-item-left">
                      <div>
                        <div className="incoming-item-name">{item.name || "Item"}</div>
                        {item.modifiers && (
                          <div className="incoming-item-modifiers">{item.modifiers}</div>
                        )}
                        {item.notes && <div className="incoming-item-notes">“{item.notes}”</div>}
                        <div className="incoming-item-price-per">
                          {item.price != null ? `${formatCurrency(item.price)} each` : "--"}
                        </div>
                      </div>
                    </div>

                    <div className="incoming-item-actions">
                      {canEdit ? (
                        <>
                          <div className="incoming-item-qty-controls">
                            <button
                              type="button"
                              className="incoming-qty-button"
                              onClick={() => handleDecrease(idx)}
                            >
                              –
                            </button>
                            <span className="incoming-qty-value">{item.quantity || 1}</span>
                            <button
                              type="button"
                              className="incoming-qty-button"
                              onClick={() => handleIncrease(idx)}
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            className="incoming-item-remove"
                            onClick={() => handleRemove(idx)}
                          >
                            Remove
                          </button>
                          <div className="incoming-item-price">
                            {lineTotal != null ? formatCurrency(lineTotal) : "--"}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="incoming-item-qty">{item.quantity || 1} ×</div>
                          <div className="incoming-item-price">
                            {lineTotal != null ? formatCurrency(lineTotal) : "--"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="order-editor-summary">
          <div className="incoming-subtotal-row">
            <span>Subtotal</span>
            <span>{formatCurrency(totals.subtotal)}</span>
          </div>
          {totals.preservedNonTaxCharges !== 0 ? (
            <div className="incoming-subtotal-row">
              <span>Other charges preserved</span>
              <span>{formatCurrency(totals.preservedNonTaxCharges)}</span>
            </div>
          ) : null}
          <div className="incoming-subtotal-row order-editor-total">
            <span>Total</span>
            <span>{formatCurrency(totals.total)}</span>
          </div>
        </div>

        <div className="order-editor-footer">
          <button type="button" className="incoming-adjust-button" onClick={onClose}>
            Close
          </button>

          {canEdit ? (
            <button
              type="button"
              className="incoming-accept-button gradient"
              onClick={handleSave}
              disabled={!canEdit || saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          ) : null}
        </div>

        {!canEdit && (
          <div className="order-editor-readonly">Order can no longer be edited.</div>
        )}
      </div>
    </div>
  );
};

export default OrderEditorDrawer;
